import React from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import './SurveyResultPage.css';
import { usePurchaseService } from '../services/PurchaseService';
import { useAuth } from '../context/AuthContext';
import SignUpForm from '../components/SignUpForm';
import { claimAnonSurvey } from '../services/SurveyService';
import { useState } from 'react';
import { getUserFacingErrorMessage } from '../services/errors';
import { initiateTempAnonSurveyPurchase } from '../services/TempService';

function UserIcon({ size = 18, color = '#e8f5f2' }) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' aria-hidden>
      <circle cx='12' cy='8' r='4' stroke={color} strokeWidth='1.5' />
      <path
        d='M4 20c0-4.418 3.582-8 8-8s8 3.582 8 8'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

function CalendarIcon({ size = 18, color = '#e8f5f2' }) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' aria-hidden>
      <rect
        x='3'
        y='5'
        width='18'
        height='16'
        rx='2'
        stroke={color}
        strokeWidth='1.5'
      />
      <path
        d='M8 3v4M16 3v4M3 10h18'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

function FlaskIcon({ size = 16, color = 'var(--color-teal-900)' }) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' aria-hidden>
      <path
        d='M10 2h4M12 2v6l6 9a4 4 0 0 1-3.3 6H9.3A4 4 0 0 1 6 17l6-9'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
      />
      <path
        d='M9 13h6'
        stroke={color}
        strokeWidth='1.5'
        strokeLinecap='round'
      />
    </svg>
  );
}

function LockIcon({ size = 16, color = 'white' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      aria-hidden
      style={{ marginRight: 8 }}
    >
      <rect
        x='4'
        y='10'
        width='16'
        height='10'
        rx='2'
        stroke={color}
        strokeWidth='1.8'
      />
      <path
        d='M8 10V8a4 4 0 0 1 8 0v2'
        stroke={color}
        strokeWidth='1.8'
        strokeLinecap='round'
      />
    </svg>
  );
}

function CheckIcon({ size = 16, color = 'var(--color-gold)' }) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill='none' aria-hidden>
      <circle cx='12' cy='12' r='10' fill={color} opacity='0.25' />
      <path
        d='M7 12.5l3.5 3.5L17 9'
        stroke={color}
        strokeWidth='2.2'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function EnneagramSymbolSVG({ size = 260, fill = 'var(--color-teal-800)' }) {
  const r = size / 2;
  const cx = r;
  const cy = r;
  const points = Array.from({ length: 9 }, (_, i) => {
    const angle = -Math.PI / 2 + i * ((2 * Math.PI) / 9);
    return [cx + (r - 8) * Math.cos(angle), cy + (r - 8) * Math.sin(angle)];
  });
  const starStep = 4; // creates a nice 9-point star
  const starPoints = points.map((_, i) => points[(i * starStep) % 9]);

  const toStr = (arr) => arr.map((p) => p.join(',')).join(' ');

  return (
    <svg
      className='enneagram-svg'
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden
    >
      <defs>
        <radialGradient id='eg-bg' cx='50%' cy='50%' r='60%'>
          <stop offset='0%' stopColor={fill} stopOpacity='0.95' />
          <stop offset='100%' stopColor={fill} stopOpacity='0.8' />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r - 2} fill='url(#eg-bg)' />
      <circle
        cx={cx}
        cy={cy}
        r={r - 6}
        fill='none'
        stroke='rgba(255,255,255,0.85)'
        strokeWidth='2'
      />
      <polygon
        points={toStr(starPoints)}
        fill='none'
        stroke='rgba(255,255,255,0.9)'
        strokeWidth='2'
      />
      {/* secondary chords for geometric overlay */}
      {points.map((p, i) => {
        const j = (i + 2) % 9;
        const k = (i + 5) % 9;
        const [x1, y1] = p;
        const [x2, y2] = points[j];
        const [x3, y3] = points[k];
        return (
          <g key={i}>
            <line
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke='rgba(255,255,255,0.35)'
              strokeWidth='1.2'
            />
            <line
              x1={x1}
              y1={y1}
              x2={x3}
              y2={y3}
              stroke='rgba(255,255,255,0.35)'
              strokeWidth='1.2'
            />
          </g>
        );
      })}
    </svg>
  );
}

function renderBriefMarkdown(md) {
  if (!md) return null;
  const lines = String(md).split(/\r?\n/);

  // Build ordered blocks to preserve original sequence
  const blocks = [];
  let paraBuf = [];
  let listBuf = [];
  const flushPara = () => {
    if (paraBuf.length) {
      const text = paraBuf.join(' ').replace(/\s+/g, ' ').trim();
      if (text) blocks.push({ type: 'p', text });
      paraBuf = [];
    }
  };
  const flushList = () => {
    if (listBuf.length) {
      blocks.push({ type: 'ul', items: listBuf.slice() });
      listBuf = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      flushList();
      continue;
    }
    if (line.startsWith('# ')) {
      flushPara();
      flushList();
      blocks.push({ type: 'h1', text: line.replace(/^#\s+/, '') });
      continue;
    }
    if (line.startsWith('### ')) {
      flushPara();
      flushList();
      blocks.push({ type: 'h3', text: line.replace(/^###\s+/, '') });
      continue;
    }
    if (/^[-*]\s+/.test(line)) {
      flushPara();
      listBuf.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }
    // default: paragraph content
    if (listBuf.length) flushList();
    paraBuf.push(line);
  }
  flushPara();
  flushList();

  // Extract first paragraph as the user-specific summary
  const firstPIndex = blocks.findIndex((b) => b.type === 'p');
  const headerBlocks = blocks.filter((b) => b.type === 'h1' || b.type === 'h3');
  const afterFirstP = firstPIndex >= 0 ? blocks.slice(firstPIndex + 1) : blocks;
  const firstP = firstPIndex >= 0 ? blocks[firstPIndex] : null;

  const elements = [];

  // Render any headings in original order before the lead paragraph
  headerBlocks.forEach((b, i) => {
    if (b.type === 'h1') {
      elements.push(
        <h2 key={`h1-${i}`} className='brief-h1'>
          {b.text}
        </h2>,
      );
    } else if (b.type === 'h3') {
      elements.push(
        <h4 key={`h3-${i}`} className='brief-h3'>
          {b.text}
        </h4>,
      );
    }
  });

  // Lead paragraph (user-specific summary) as normal paragraph
  if (firstP) {
    elements.push(
      <p key='lead' className='brief-p'>
        {firstP.text}
      </p>,
    );
  }

  // Divider between summary and promo details
  if (afterFirstP.length) {
    elements.push(<hr key='divider' className='brief-divider' />);
  }

  // Render the rest (promo content) preserving order, excluding headings already rendered
  afterFirstP.forEach((b, idx) => {
    if (b.type === 'p') {
      elements.push(
        <p key={`pp-${idx}`} className='brief-p'>
          {b.text}
        </p>,
      );
    } else if (b.type === 'ul') {
      elements.push(
        <ul key={`ul-${idx}`} className='brief-list'>
          {b.items.map((it, i) => (
            <li key={`li-${idx}-${i}`}>{it}</li>
          ))}
        </ul>,
      );
    }
  });

  return elements;
}

function formatSubmittedAt(ts) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(ts));
  } catch {
    return new Date(ts).toLocaleString();
  }
}

export default function SurveyResultPage() {
  const location = useLocation();
  const result = location.state?.result;
  const navigate = useNavigate();
  const { initiateSurveyUnlock } = usePurchaseService();
  const { userName } = useAuth();
  const { token } = useAuth();
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [localResult, setLocalResult] = useState(result);
  const [isProcessing, setIsProcessing] = useState(false);

  async function handleAnonTempUnlock() {
    setIsProcessing(true);
    await new Promise((res) => requestAnimationFrame(res));
    try {
      let storedTemp = null;
      try {
        storedTemp = localStorage.getItem('anonTempSurvey');
      } catch {}
      const temp = storedTemp ? JSON.parse(storedTemp) : null;
      const tempId = temp?.tempId || localResult?.tempId;
      if (!tempId) {
        throw new Error(
          'Missing tempId. Please retake the survey to unlock full results.',
        );
      }

      const purchaseResp = await initiateTempAnonSurveyPurchase({
        tempId,
        price: 19.95,
      });

      const clientSecret = purchaseResp?.clientSecret;
      const sessionId = purchaseResp?.sessionId;
      if (clientSecret) {
        try {
          localStorage.setItem(
            'anonTempPurchase',
            JSON.stringify({ tempId, clientSecret, sessionId }),
          );
        } catch {}
      }

      navigate('/payment', {
        state: {
          message: 'Payment session created.',
          clientSecret,
          sessionId,
        },
      });
    } catch (e) {
      console.error('Anonymous temp unlock failed', e);
      // For service-layer ApiErrors, show safe message.
      if (e?.name === 'ApiError') {
        alert(getUserFacingErrorMessage(e, e.context || 'purchaseInitiate'));
      } else {
        alert(e?.message || 'Failed to initiate purchase. Please try again.');
      }
    } finally {
      setIsProcessing(false);
    }
  }

  // Debug: log key state so we can see why the left column may be empty
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem('anonClaim');
      const storedTemp = localStorage.getItem('anonTempSurvey');
      console.debug('SurveyResultPage:init', {
        locationStateResult: result,
        localResultInitial: localResult,
        anonClaimStorage: stored ? JSON.parse(stored) : null,
        anonTempSurveyStorage: storedTemp ? JSON.parse(storedTemp) : null,
        tokenPresent: Boolean(token),
      });
    } catch (e) {
      console.debug('SurveyResultPage:init: failed to read anonClaim', e);
    }
  }, []);

  // If navigated without state (or brief text missing), try to hydrate from
  // localStorage anonClaim so anonymous results still display after redirect/reload.
  React.useEffect(() => {
    if (localResult && localResult.briefResultText) return;
    try {
      // New anonymous temp flow
      const storedTemp = localStorage.getItem('anonTempSurvey');
      if (storedTemp) {
        const temp = JSON.parse(storedTemp);
        if (temp && (temp.rBriefResult || temp.briefResult)) {
          setLocalResult((prev) => ({
            ...(prev || {}),
            briefResultText:
              prev?.briefResultText || temp.rBriefResult || temp.briefResult,
            tempId: prev?.tempId || temp.tempId,
            expiresAt: prev?.expiresAt || temp.expiresAt,
          }));
          return;
        }
      }

      // Legacy anon claim flow (kept for backward compatibility)
      const stored = localStorage.getItem('anonClaim');
      if (!stored) return;
      const claim = JSON.parse(stored);
      if (claim && (claim.rBrief || claim.rBriefResult || claim.brief)) {
        setLocalResult((prev) => ({
          ...(prev || {}),
          briefResultText:
            prev?.briefResultText ||
            claim.rBrief ||
            claim.rBriefResult ||
            claim.brief,
          claimId: prev?.claimId || claim.claimId,
          claimCode: prev?.claimCode || claim.claimCode,
          expiresAt: prev?.expiresAt || claim.expiresAt,
        }));
      }
    } catch (e) {
      console.warn('Failed to hydrate anon result from storage', e);
    }
  }, [localResult]);

  // Listen for processing events dispatched by other components (NavBar)
  React.useEffect(() => {
    const onStart = () => setIsProcessing(true);
    const onEnd = () => setIsProcessing(false);
    try {
      window.addEventListener('surveyProcessingStart', onStart);
      window.addEventListener('surveyProcessingEnd', onEnd);
    } catch (e) {
      // ignore in non-browser environments
    }
    return () => {
      try {
        window.removeEventListener('surveyProcessingStart', onStart);
        window.removeEventListener('surveyProcessingEnd', onEnd);
      } catch (e) {}
    };
  }, []);

  // If no result is provided, keep a simple fallback
  if (!localResult) {
    return (
      <div className='result-fallback'>
        <div className='fallback-box'>
          <h2>Survey Result</h2>
          <p>No result to display. Please take the survey first.</p>
          <Link to='/'>Go to Home</Link>
        </div>
      </div>
    );
  }

  async function handleUnlock() {
    // Show processing overlay and allow it to render
    setIsProcessing(true);
    console.debug('SurveyResultPage: unlock processing start');
    await new Promise((res) => requestAnimationFrame(res));
    try {
      const resp = await initiateSurveyUnlock({
        price: 19.95,
        surveyResponseId: localResult.responseId,
      });
      const message = resp?.message || 'Payment session created.';
      navigate('/payment', {
        state: {
          message,
          purchaseId: resp?.purchaseId,
          sessionId: resp?.sessionId,
          clientSecret: resp?.clientSecret,
          surveyResponseId: resp?.surveyResponseId,
        },
      });
    } catch (e) {
      navigate('/payment', {
        state: { message: getUserFacingErrorMessage(e, 'purchaseInitiate') },
      });
    } finally {
      setIsProcessing(false);
      console.debug('SurveyResultPage: unlock processing end');
    }
  }

  return (
    <div className={`result-page ${isProcessing ? 'blurred' : ''}`}>
      {/* <header className='result-header'>
        <div className='header-content'>
          <div className='header-texts'>
            <h1 className='result-title'>THE INNERSCIENCE PERSONALITY TEST</h1>
            <div className='user-bar'>
              <span className='icon'>
                <UserIcon />
              </span>
              <span className='user-label'>
                {`RESULTS FOR ${(userName || 'YOU').toUpperCase()}`}
              </span>
              <span className='sep' />
              <span className='icon'>
                <CalendarIcon />
              </span>
              <span className='date-label'>
                {localResult?.submittedAt
                  ? formatSubmittedAt(localResult.submittedAt).toUpperCase()
                  : ''}
              </span>
            </div>
          </div>
          <div className='header-graphics'>
            <div className='badge'>
              <div className='badge-icon'>
                <FlaskIcon color={'var(--color-teal-900)'} />
              </div>
              <div className='badge-text'>
                ACCURATE, INSIGHTFUL PERSONALITY TESTS
              </div>
            </div>
            <div className='enneagram-graphic'>
              <EnneagramSymbolSVG />
            </div>
          </div>
        </div>
      </header> */}

      <main className='result-main'>
        <div className='content-two-col'>
          {/* Left Column: Brief Summary */}
          <aside className='left-rail'>
            {localResult.briefResultText && (
              <section className='section summary'>
                <div className='brief-box'>
                  <div className='section-title'>
                    Your InnerScience Personality Result
                  </div>
                  <div className='brief-text'>
                    {renderBriefMarkdown(localResult.briefResultText)}
                  </div>
                  {/* Floating CTA anchored around the 85% mark, does not affect layout */}
                  {/* <div className='brief-overlay-cta>
                    <button className='cta-btn alt' onClick={handleUnlock}>
                      <LockIcon />
                      UNLOCK MY FULL REPORT
                    </button>
                  </div> */}
                </div>
              </section>
            )}
          </aside>

          {/* Right Column: Superpowers + Description */}
          <section className='right-rail'>
            <section className='section results'>
              <h3 className='section-subtitle'>
                About InnerScience Personality Full Results
              </h3>
              <p className='muted'>
                Your InnerScience Personality Report reflects how your traits
                and states can shift across different life contexts. Rather than
                assuming you are always the same, it highlights how you tend to
                show up when things are going well and how patterns may change
                under stress or emotional strain.
              </p>
            </section>

            <section className='section about'>
              <h3 className='section-subtitle'>
                Understanding Your Personality in Context
              </h3>
              <p className='muted'>
                The results of your InnerScience Personality Report offer a
                dynamic picture of how your personality shifts depending on what
                you’re facing. When you feel safe, energized, or in control,
                certain strengths and preferences come forward. Under stress,
                emotional strain, or triggering situations, other tendencies may
                emerge. These shifts are not flaws—they are part of being human.
              </p>
              <p className='muted'>
                The purpose of this test is to build insight—not just into who
                you are, but into how you are. By recognizing your patterns
                across different states, you gain tools to shift in healthier,
                more adaptive directions. With greater self-awareness, you can
                choose responses that align with your goals, support your
                relationships, and foster lasting personal growth.
              </p>
              <section className='section superpowers'>
                <h2 className='section-title'>How your full report can help</h2>
                <div className='features-card'>
                  <ul className='checklist'>
                    <li>
                      <CheckIcon /> Typically 30-37 pages of accurate, personal
                      observations
                    </li>
                    <li>
                      <CheckIcon /> Discover your patterns across contexts and
                      growth paths
                    </li>
                    <li>
                      <CheckIcon /> Tap into your superpowers and blind spots
                    </li>
                    <li>
                      <CheckIcon /> Understand how stress affects you
                    </li>
                  </ul>
                </div>
              </section>

              {token ? (
                <button className='cta-btn alt' onClick={handleUnlock}>
                  <LockIcon />
                  UNLOCK MY FULL REPORT
                </button>
              ) : (
                <>
                  <div className='register-prompt'>
                    <button
                      className='cta-btn alt'
                      onClick={handleAnonTempUnlock}
                    >
                      See Full Interpretive Results for Only $19.95
                    </button>
                  </div>

                  {showRegisterModal && (
                    <div className='modal-backdrop'>
                      <div className='modal'>
                        <button
                          className='modal__close'
                          aria-label='Close register'
                          title='Close'
                          type='button'
                          onClick={() => setShowRegisterModal(false)}
                        >
                          ×
                        </button>
                        <SignUpForm
                          onSignUp={async (data) => {
                            // After successful signup, attempt to claim the anon result
                            const tok =
                              data?.token ||
                              localStorage.getItem('token') ||
                              token;
                            const stored = localStorage.getItem('anonClaim');
                            let claim = null;
                            try {
                              claim = stored ? JSON.parse(stored) : null;
                            } catch (e) {
                              console.warn(
                                'Failed to parse anonClaim from storage',
                                e,
                              );
                            }
                            if (!claim || !claim.claimId) {
                              setShowRegisterModal(false);
                              return;
                            }
                            // show processing overlay and allow a frame to render
                            setIsProcessing(true);
                            console.debug('SurveyResultPage: processing start');
                            await new Promise((res) =>
                              requestAnimationFrame(res),
                            );
                            try {
                              const claimed = await claimAnonSurvey(
                                claim.claimId,
                                claim.claimCode,
                                tok,
                              );
                              console.debug(
                                'SurveyResultPage: claimed response',
                                claimed,
                              );
                              // Update local result to reflect claimed/linked survey
                              setLocalResult((prev) => ({
                                ...prev,
                                ...claimed,
                                briefResultText:
                                  claimed.briefResultText ||
                                  prev.briefResultText,
                              }));
                              // Clear local anon claim
                              try {
                                localStorage.removeItem('anonClaim');
                              } catch {}

                              // Try to initiate purchase to unlock the full report even if
                              // we couldn't find an explicit surveyResponseId in the claim.
                              try {
                                const surveyResponseId =
                                  claimed.responseId ||
                                  claimed.surveyResultId ||
                                  claimed.survey_response_id ||
                                  claimed.id;
                                const purchaseResp = await initiateSurveyUnlock(
                                  {
                                    price: 19.95,
                                    surveyResponseId,
                                    claimId: claimed.claimId,
                                  },
                                );
                                console.debug(
                                  'SurveyResultPage: initiateSurveyUnlock response',
                                  purchaseResp,
                                );
                                const message =
                                  purchaseResp?.message ||
                                  'Payment session created.';
                                const hasSession =
                                  purchaseResp?.sessionId ||
                                  purchaseResp?.purchaseId ||
                                  purchaseResp?.clientSecret;
                                if (hasSession) {
                                  // Navigate to payment page with the purchase/session details
                                  navigate('/payment', {
                                    state: {
                                      message,
                                      purchaseId: purchaseResp?.purchaseId,
                                      sessionId: purchaseResp?.sessionId,
                                      clientSecret: purchaseResp?.clientSecret,
                                      surveyResponseId:
                                        purchaseResp?.surveyResponseId ||
                                        surveyResponseId,
                                    },
                                  });
                                  return;
                                }
                                // If no session info returned, fall through to show claimed result
                              } catch (payErr) {
                                console.error(
                                  'Failed to initiate purchase after claim',
                                  payErr,
                                );
                                // Fall through — we'll close modal and stay on page showing claimed result
                              }
                            } catch (e) {
                              console.error('Failed to claim anon result', e);
                            } finally {
                              setShowRegisterModal(false);
                              setIsProcessing(false);
                              console.debug('SurveyResultPage: processing end');
                            }
                          }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </section>
        </div>

        {/* Optional full-width report mockup below the two-column layout
        <section className='section report'>
          <div className='report-mockup'>
            <div className='report-cover'>
              <div className='cover-title'>Your InnerScience Report</div>
              <div className='cover-symbol'>
                <EnneagramSymbolSVG size={140} fill={'var(--color-teal-800)'} />
              </div>
            </div>
          </div>
        </section> */}
      </main>
      {isProcessing && (
        <div className='processing-overlay' role='status' aria-live='polite'>
          <div className='processing-card'>
            <div className='spinner' aria-hidden />
            <div className='processing-text'>
              Preparing your full report, please wait.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
